import { DateTime } from 'luxon';

import {
	BOOKING_DEADLINE_HOURS,
	CANCELLATION_DEADLINE_MINUTES,
	SLOT_MINUTES,
	TIME_ZONE,
} from '@common/constants/academyReworkings';
import {
	AvailableSlot,
	BookedMeet,
	BookingAssignment,
	MissedLesson,
	SelectedTime,
	SlotTeacher,
	TimeWindow,
} from '@common/types/academyReworkings';

export function parseDate(value: string) {
	const options = { zone: TIME_ZONE };
	const sql = DateTime.fromSQL(value, options);
	return sql.isValid ? sql : DateTime.fromISO(value, options);
}

export function teacherName(teacher: SlotTeacher) {
	return [teacher.first_name, teacher.last_name].filter(Boolean).join(' ') || String(teacher.id);
}

export function bookingLessonName(booking: BookedMeet, fallback: string) {
	const scheduled = booking.gradebook?.schedule_lesson;
	return scheduled?.lesson?.name || scheduled?.name || fallback;
}

export function timeLabel(window: TimeWindow) {
	return `${parseDate(window.start).toFormat('HH:mm')}–${parseDate(window.end).toFormat('HH:mm')}`;
}

export function canBook(window: Pick<TimeWindow, 'start'>, now = DateTime.now()) {
	return parseDate(window.start).diff(now, 'hours').hours > BOOKING_DEADLINE_HOURS;
}

export function canCancelBooking(start: string, now = DateTime.now()) {
	return parseDate(start).diff(now, 'minutes').minutes >= CANCELLATION_DEADLINE_MINUTES;
}

export function groupSlots(slots: AvailableSlot[]): TimeWindow[] {
	const windows = new Map<string, TimeWindow>();
	slots.forEach((slot) => {
		const start = parseDate(slot.start_dt);
		const end = parseDate(slot.end_dt);
		if (
			slot.status !== 0 ||
			!slot.teacher?.id ||
			!start.isValid ||
			!end.isValid ||
			end.diff(start, 'minutes').minutes !== SLOT_MINUTES
		) return;
		const key = `${start.toMillis()}:${end.toMillis()}`;
		const window = windows.get(key) || { key, start: slot.start_dt, end: slot.end_dt, slots: [] };
		if (!window.slots.some((item) => item.teacher.id === slot.teacher.id)) window.slots.push(slot);
		windows.set(key, window);
	});
	windows.forEach((window) => {
		window.slots.sort((a, b) => {
			const chargeA = Number.isFinite(a.teacher.charge) ? a.teacher.charge : Infinity;
			const chargeB = Number.isFinite(b.teacher.charge) ? b.teacher.charge : Infinity;
			return chargeA - chargeB || a.teacher.id - b.teacher.id || a.id - b.id;
		});
	});
	return Array.from(windows.values()).sort(
		(a, b) => parseDate(a.start).toMillis() - parseDate(b.start).toMillis(),
	);
}

export function overlaps(
	a: Pick<TimeWindow, 'start' | 'end'>,
	b: Pick<TimeWindow, 'start' | 'end'>,
) {
	return parseDate(a.start) < parseDate(b.end) && parseDate(b.start) < parseDate(a.end);
}

export function hasBookingConflict(
	window: Pick<TimeWindow, 'start' | 'end'>,
	bookings: BookedMeet[],
) {
	return bookings.some(
		(booking) =>
			booking.service_id === 1 &&
			[0, 3].includes(booking.status) &&
			overlaps(window, { start: booking.start_dt, end: booking.end_dt }),
	);
}

export function assignments(lessons: MissedLesson[], times: SelectedTime[]): BookingAssignment[] {
	const sorted = [...times].sort(
		(a, b) => parseDate(a.window.start).toMillis() - parseDate(b.window.start).toMillis(),
	);
	return lessons.flatMap((lesson, index) => {
		const time = sorted[index];
		const slot = time?.window.slots.find((item) => item.id === time.slotId);
		return slot ? [{ lesson, slot }] : [];
	});
}

export function bookingPayload(
	assignment: BookingAssignment,
	context: {
		studentId: number;
		locationId: number;
		phone: string;
		comment: string;
	},
) {
	const { slot, lesson } = assignment;
	return {
		type: 'working_off',
		service_id: 1,
		location_id: context.locationId,
		student_id: context.studentId,
		meet_slot_id: slot.id,
		teacher_id: slot.teacher.id,
		gradebook_id: lesson.gradebook_id,
		program_group_id: lesson.program_group_id,
		program: lesson.program_id,
		phone: context.phone,
		comment_student: context.comment,
		zoom_link: slot.teacher.zoom_link,
		zoom_id: slot.teacher.zoom_id,
		zoom_access_code: slot.teacher.zoom_access_code,
		miro_link: slot.teacher.miro_link,
	};
}
